import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher';
import AuthModal from '../AuthModal';
import { Menu, X } from 'lucide-react';
import { useSiteContent } from '../home/siteContent';
import { BrandButton, SmartLink } from '../home/ui';
import '../home/home.css';

const SiteHeader = () => {
  const { user } = useAuth();
  const { content } = useSiteContent();
  const { topbar, nav } = content;
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  // Desktop row only when the translated labels really fit; otherwise the hamburger menu.
  const [collapsed, setCollapsed] = useState(true);
  const rowRef = useRef(null);
  const logoRef = useRef(null);
  const measureRef = useRef(null);

  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row || !logoRef.current || !measureRef.current) return;
    const cs = window.getComputedStyle(row);
    const inner = row.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const available = inner - logoRef.current.offsetWidth - 24;
    setCollapsed(measureRef.current.offsetWidth > available);
  }, []);

  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (rowRef.current) ro.observe(rowRef.current);
    if (document.fonts?.ready) document.fonts.ready.then(measure);
    return () => ro.disconnect();
  }, [measure, content, user]);

  const openAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setMobileOpen(false);
  };

  const isActive = (to) => location.pathname === to;

  const renderNav = (measureOnly) => (
          <nav className={`${measureOnly ? 'flex' : collapsed ? 'hidden' : 'flex'} min-w-0 flex-1 items-center justify-center gap-[clamp(14px,1.5vw,40px)] whitespace-nowrap pl-[clamp(12px,2vw,60px)]`}>
            {nav.links.map((link) => (
              <SmartLink
                key={link.to + link.label}
                to={link.to}
                onAuth={openAuth}
                data-testid={measureOnly ? undefined : `header-nav-${link.to.replace(/\//g, '') || 'home'}`}
                className={`text-[clamp(14px,0.95vw,17px)] font-semibold leading-[15px] transition-colors hover:text-brand ${
                  isActive(link.to) ? 'text-brand-ink' : 'text-[#263946]'
                }`}
              >
                {link.label}
              </SmartLink>
            ))}
          </nav>
  );

  const renderActions = (measureOnly) => (
          <div className={`${measureOnly ? 'flex' : collapsed ? 'hidden' : 'flex'} ml-auto flex-shrink-0 items-center gap-[clamp(10px,0.7778vw,14px)] whitespace-nowrap`}>
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                data-testid={measureOnly ? undefined : 'header-dashboard-button'}
                className="hp-btn hp-btn-solid"
              >
                {nav.dashboard_label}
                <span aria-hidden className="hp-arrow" />
              </button>
            ) : (
              <>
                <BrandButton
                  to="login"
                  onAuth={openAuth}
                  variant="outline"
                  data-testid={measureOnly ? undefined : 'header-login-button'}
                  className="min-w-[clamp(100px,7vw,140px)]"
                >
                  {nav.login_label}
                </BrandButton>
                <BrandButton
                  to="register"
                  onAuth={openAuth}
                  data-testid={measureOnly ? undefined : 'header-register-button'}
                  className="px-[clamp(18px,1.7222vw,31px)]"
                >
                  {nav.cta_label}
                </BrandButton>
              </>
            )}
          </div>
  );

  return (
    <>
      {/* Service bar */}
      <div id="top" className="hp bg-brand-mist" data-testid="site-topbar">
        <div className="hp-container flex h-[clamp(44px,3.3889vw,61px)] items-center">
          <ul className="hidden flex-1 items-center justify-around pr-[22px] md:flex">
            {topbar.items.map((item) => (
              <li
                key={item}
                className="flex items-center gap-2.5 text-[clamp(13px,0.8889vw,16px)] font-medium leading-[15px] text-brand-navy"
              >
                <img src="/brand/icon-check.svg" alt="" aria-hidden className="h-5 w-5" />
                {item}
              </li>
            ))}
          </ul>
          <div className="flex flex-1 items-center justify-end gap-[clamp(20px,2.7222vw,49px)] md:flex-none">
            <Link
              to="/faq"
              className="text-[clamp(15px,1vw,18px)] font-semibold leading-[15px] text-brand-navy transition-colors hover:text-brand"
            >
              {topbar.faq_label}
            </Link>
            <LanguageSwitcher variant="brand" />
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <header className="hp sticky top-0 z-50 border-b border-[#C0C0C0]/40 bg-white" data-testid="site-header">
        <div ref={rowRef} className="hp-container relative flex h-[clamp(72px,6.3889vw,115px)] items-center">
          <Link ref={logoRef} to="/" className="flex-shrink-0" data-testid="header-logo-link">
            <img
              src="/brand/logo.png"
              alt="WowProfit.io"
              className="h-[clamp(44px,4.4444vw,80px)] w-auto"
            />
          </Link>

          {renderNav(false)}

          {renderActions(false)}

          {/* Invisible copy at natural width: decides whether the desktop row fits. */}
          <div ref={measureRef} aria-hidden="true" inert="" className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center">
            {renderNav(true)}
            {renderActions(true)}
          </div>

          <button
            className={`${collapsed ? '' : 'hidden'} ml-auto p-2 text-brand-navy`}
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="header-mobile-toggle"
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>

        {mobileOpen && collapsed && (
          <div
            className={`animate-fade-in border-t border-[#C0C0C0]/40 bg-white ${collapsed ? '' : 'hidden'}`}
            data-testid="header-mobile-menu"
          >
            <div className="hp-container py-5">
              <nav className="flex flex-col">
                {nav.links.map((link) => (
                  <SmartLink
                    key={link.to + link.label}
                    to={link.to}
                    onAuth={openAuth}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`header-mobile-nav-${link.to.replace(/\//g, '') || 'home'}`}
                    className={`border-b border-brand-line py-3.5 text-[17px] font-semibold last:border-0 ${
                      isActive(link.to) ? 'text-brand-ink' : 'text-brand-navy'
                    }`}
                  >
                    {link.label}
                  </SmartLink>
                ))}
              </nav>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {user ? (
                  <button onClick={() => navigate('/dashboard')} className="hp-btn hp-btn-solid w-full">
                    {nav.dashboard_label}
                    <span aria-hidden className="hp-arrow" />
                  </button>
                ) : (
                  <>
                    <BrandButton to="login" onAuth={openAuth} variant="outline" className="w-full" data-testid="mobile-login-button">
                      {nav.login_label}
                    </BrandButton>
                    <BrandButton to="register" onAuth={openAuth} className="w-full" data-testid="mobile-register-button">
                      {nav.cta_label}
                    </BrandButton>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />
    </>
  );
};

export default SiteHeader;
